import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
    applyNoIndexSeo,
    applySeo,
    getBasePathFromArabic,
    isArabicPath,
} from "../../util/seo";
import {
    getSeoConfigForPath,
    getArabicSeoConfigForPath,
} from "../../Pages/SeoPages/seoPageData";
import { getPSeoConfigForPath } from "../../Pages/SeoPages/pSeoPageLoader";

const HOME_DESCRIPTION =
    "Turn ideas into measurable impact with Compelion's AI-powered innovation management platform for idea evaluation, project delivery, analytics, and collaboration.";

const HOME_DESCRIPTION_AR =
    "حوّل الأفكار إلى أثر قابل للقياس مع منصة Compelion للإدارة الابتكارية المدعومة بالذكاء الاصطناعي لتقييم الأفكار وتسليم المشاريع والتحليلات والتعاون.";

const HOME_STRUCTURED_DATA = [
    {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Compelion",
        url: "https://compelion.com",
        logo: "https://compelion.com/logo.png",
        description: HOME_DESCRIPTION,
    },
    {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Compelion",
        url: "https://compelion.com",
        description: HOME_DESCRIPTION,
    },
];

const STATIC_PUBLIC_ROUTES = {
    "/": {
        title: "Compelion | AI Innovation Management and Project Delivery Platform",
        description: HOME_DESCRIPTION,
        canonicalPath: "/",
        structuredData: HOME_STRUCTURED_DATA,
    },
    "/about-us": {
        title: "About Compelion | AI Innovation Management Experts",
        description:
            "Learn how Compelion helps organisations evaluate ideas, align strategy, and deliver innovation outcomes with AI-powered workflows and expert guidance.",
        canonicalPath: "/about-us",
    },
    "/pricing": {
        title: "Pricing | Compelion Innovation Management Platform",
        description:
            "Explore Compelion pricing plans for teams, growing organisations, and enterprises using AI-powered innovation management and delivery tools.",
        canonicalPath: "/pricing",
    },
    "/security": {
        title: "Security | Compelion",
        description:
            "Review Compelion's approach to platform security, data protection, and operational safeguards for modern innovation and project delivery teams.",
        canonicalPath: "/security",
    },
    "/contact-us": {
        title: "Contact Compelion | Speak With Our Team",
        description:
            "Contact Compelion to discuss innovation management, platform access, implementation support, or enterprise requirements.",
        canonicalPath: "/contact-us",
    },
    "/blogs": {
        title: "Compelion Blog | Innovation, Strategy and Delivery Insights",
        description:
            "Read Compelion insights on innovation management, strategy execution, project delivery, analytics, and digital transformation.",
        canonicalPath: "/blogs",
    },
    "/idea-evaluation": {
        title: "Idea Evaluation | Compelion",
        description:
            "Evaluate new ideas with Compelion and turn early concepts into structured innovation plans, decisions, and actionable next steps.",
        canonicalPath: "/idea-evaluation",
    },
    "/schedule-consultation": {
        title: "Schedule a Consultation | Compelion",
        description:
            "Book a consultation with Compelion to discuss your innovation goals, delivery challenges, and how the platform can support your organisation.",
        canonicalPath: "/schedule-consultation",
    },
    "/privacy-policy": {
        title: "Privacy Policy | Compelion",
        description:
            "Read the Compelion privacy policy for information on data handling, usage, retention, and user rights.",
        canonicalPath: "/privacy-policy",
    },
    "/terms-and-conditions": {
        title: "Terms and Conditions | Compelion",
        description:
            "Review the Compelion terms and conditions for platform usage, access, responsibilities, and legal terms.",
        canonicalPath: "/terms-and-conditions",
    },
};

const STATIC_PUBLIC_ROUTES_AR = {
    "/ar": {
        title: "Compelion | منصة إدارة الابتكار وتسليم المشاريع بالذكاء الاصطناعي",
        description: HOME_DESCRIPTION_AR,
        canonicalPath: "/ar",
        lang: "ar",
        alternatePaths: { en: "/", ar: "/ar" },
        structuredData: HOME_STRUCTURED_DATA,
    },
    "/ar/about-us": {
        title: "عن Compelion | خبراء إدارة الابتكار بالذكاء الاصطناعي",
        description:
            "تعرّف على كيفية مساعدة Compelion للمؤسسات في تقييم الأفكار ومواءمة الاستراتيجية وتحقيق نتائج الابتكار باستخدام سير العمل المدعوم بالذكاء الاصطناعي.",
        canonicalPath: "/ar/about-us",
        lang: "ar",
        alternatePaths: { en: "/about-us", ar: "/ar/about-us" },
    },
    "/ar/pricing": {
        title: "الأسعار | منصة Compelion لإدارة الابتكار",
        description:
            "استكشف خطط أسعار Compelion للفرق والمؤسسات المتنامية والمؤسسات الكبرى باستخدام أدوات إدارة الابتكار والتسليم المدعومة بالذكاء الاصطناعي.",
        canonicalPath: "/ar/pricing",
        lang: "ar",
        alternatePaths: { en: "/pricing", ar: "/ar/pricing" },
    },
    "/ar/contact-us": {
        title: "تواصل مع Compelion | تحدث مع فريقنا",
        description:
            "تواصل مع Compelion لمناقشة إدارة الابتكار والوصول إلى المنصة ودعم التنفيذ أو متطلبات المؤسسات.",
        canonicalPath: "/ar/contact-us",
        lang: "ar",
        alternatePaths: { en: "/contact-us", ar: "/ar/contact-us" },
    },
    "/ar/schedule-consultation": {
        title: "حجز استشارة | Compelion",
        description:
            "احجز استشارة مع Compelion لمناقشة أهداف الابتكار وتحديات التسليم وكيف يمكن للمنصة دعم مؤسستك.",
        canonicalPath: "/ar/schedule-consultation",
        lang: "ar",
        alternatePaths: { en: "/schedule-consultation", ar: "/ar/schedule-consultation" },
    },
};

const NON_INDEXABLE_PUBLIC_ROUTES = new Set([
    "/login",
    "/register",
    "/on-boarding",
    "/password-reset",
    "/whiteboard",
]);

const getSeoConfig = (pathname) => {
    /* Arabic SEO page (e.g. /ar/ai-innovation-management-software) */
    if (isArabicPath(pathname)) {
        const basePath = getBasePathFromArabic(pathname);

        /* Check Arabic static routes first */
        if (STATIC_PUBLIC_ROUTES_AR[pathname]) {
            return STATIC_PUBLIC_ROUTES_AR[pathname];
        }

        /* Check dynamic Arabic SEO pages */
        const arConfig = getArabicSeoConfigForPath(basePath);
        if (arConfig) {
            return arConfig;
        }

        /* Arabic home */
        if (pathname === "/ar" || pathname === "/ar/") {
            return STATIC_PUBLIC_ROUTES_AR["/ar"];
        }

        return null;
    }

    /* English SEO page */
    const seoPageConfig = getSeoConfigForPath(pathname);
    if (seoPageConfig) {
        return seoPageConfig;
    }

    /* Programmatic SEO page */
    const pSeoConfig = getPSeoConfigForPath(pathname);
    if (pSeoConfig) {
        return pSeoConfig;
    }

    if (STATIC_PUBLIC_ROUTES[pathname]) {
        return STATIC_PUBLIC_ROUTES[pathname];
    }

    if (pathname.startsWith("/blogs/")) {
        return {
            title: "Compelion Blog | Innovation Insights",
            description:
                "Explore Compelion blog articles covering innovation strategy, project delivery, analytics, and organisational improvement.",
            canonicalPath: pathname,
            ogType: "article",
        };
    }

    return null;
};

function SeoRouteManager() {
    const location = useLocation();

    useEffect(() => {
        const seoConfig = getSeoConfig(location.pathname);
        if (seoConfig) {
            applySeo(seoConfig);
            return;
        }

        if (
            NON_INDEXABLE_PUBLIC_ROUTES.has(location.pathname) ||
            location.pathname.startsWith("/dashboard") ||
            location.pathname.startsWith("/superadmin")
        ) {
            applyNoIndexSeo();
        }
    }, [location.pathname]);

    return null;
}

export default SeoRouteManager;